/**
 *
 * JobCard - use to show recent inference jobs. or all jobs on history page
 *
 */
// @ts-nocheck
import React, { memo, useEffect, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import Collapsible from 'react-collapsible';
import { DisplaySongInfo } from "app/components/GlobalAudioPlayer/slice/types";
import AudioFileStreamable from "app/components/AudioFileStreamable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faCircleExclamation, faMicrophoneStand, faPause, faPlay, faThumbsUp as faThumbsUpSolid, faThumbsDown as faThumbsDownSolid, faCompactDisc, faAlignLeft, faChevronUp, faShareAltSquare, faCross, faClose, faLink, faTick, faCheck, faShare } from "@fortawesome/pro-solid-svg-icons";
import { faThumbsUp, faThumbsDown, faSquareShareNodes } from "@fortawesome/pro-light-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { useGlobalAudioSlice } from "app/components/GlobalAudioPlayer/slice";
import { Button, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from "@mui/material";
import { selectCurrentSong, selectIsPlaying } from "app/components/GlobalAudioPlayer/slice/selectors";
import { Orbit, Pulsar } from "@uiball/loaders";
import { JobCardActionsMenu } from "app/components/JobCard/JobCardActionsMenu";
import { UserProfile } from '@clerk/clerk-react';
import { useOutletContext } from 'react-router-dom'
import stemSplitIcon from 'assets/stem_split_icon.svg';
import { IconButton } from '@mui/material';
import { Api } from "store/api";
import { toast } from 'react-hot-toast';
import { useJobsSlice } from 'app/pages/JobsPage/slice';
import { faPenToSquare } from "@fortawesome/pro-solid-svg-icons";
import { UpdateUsernameModal } from '../updateUsernameModal';
import { left } from 'inquirer/lib/utils/readline';
import { ShareDialog } from '../SharedJobCard/ShareDialog';



const DEFAULT_COVER_IMAGE_URL = "https://storage.googleapis.com/controlla-public-images/ControllaVoiceLogo.png"
const DEFAULT_SAMPLER_STYLE_IMAGE_URL = "https://storage.googleapis.com/controlla-public-images/Vinyl-placeholder.jpg"

interface Props {
  job: any,
  afterExpand?: Function, // TO update explicit size of react-virtualized list items
  afterCollapse?: Function, // TO update explicit size of react-virtualized list items
  onPlayed?: Function,
  onAfterDeleteJob?: (removedJobId: string) => void,
}

function sortJobAudioFiles(a, b) {
  if (a.audio_file?.type == "mixed") return -1;
  else if (b.audio_file?.type == "mixed") return 1;
  else if (a.audio_file?.type == "vocal-inference") return -1;
  else if (b.audio_file?.type == "vocal-inference") return 1;
  return -1;
}

function getSelectedStemsStringFromStemSplitJob(job) {
  return job.func_kwargs.selected_stems.join(', ')
}

function getTruncatedLyricsFromJob(job, truncateLength) {
  const lyrics = job?.func_kwargs?.lyrics?.replace(/(\r\n|\n|\r)/gm, " ");
  if (!lyrics) return 'Instrumental';
  if (lyrics.length <= truncateLength * 2) return lyrics;
  const start = lyrics.substring(0, truncateLength);
  const end = lyrics.substring(lyrics.length - truncateLength);
  return `${start}...${end}`;
}



export const JobCard = memo((props: Props) => {
  const dispatch = useDispatch();
  const { actions: globalAudioActions } = useGlobalAudioSlice()
  const { actions: jobsActions } = useJobsSlice()
  const [userProfile, isLoaded, isSignedIn] = useOutletContext();
  const currentSong = useSelector(selectCurrentSong)
  const isPlaying = useSelector(selectIsPlaying)

  const [isDetailsCollapsed, setIsDetailsCollapsed] = useState(!props.isExpanded);
  const [lyricsModalOpen, setLyricsModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [copyLinkButtonText, setCopyLinkButtonText] = useState("COPY LINK");

  const voice_model = props.job?.voice_model
  const status = props.job.status
  let audio_file_outputs = props.job.audio_file_outputs
  const audio_file_inputs = props.job.audio_file_inputs
  const isTraining = props.job.type === "training" || props.job.type === "training-v2"
  if (isTraining) audio_file_outputs = props.job.audio_file_outputs

  const isStemSplit = props.job.type === "stem-split"
  const isLyricToMusic = props.job.type === "lyrics-to-music"
  const isSongGeneration = props.job.type === "song-generation"

  // output audio files
  const mixAudioFiles = audio_file_outputs.filter(audio_file_output => audio_file_output.type == "mixed")
  const generatedSongFiles = audio_file_outputs.filter(audio_file_output => audio_file_output.type == "generated-song")
  const instrumentalAudioFiles = audio_file_outputs.filter(audio_file_output => audio_file_output.type == "instrumental")
  const vocalAudioFiles = audio_file_outputs.filter(audio_file_output => audio_file_output.type == "vocal-inference")
  const outputAudioFiles = audio_file_outputs.length > 0 && audio_file_outputs.toSorted?.(sortJobAudioFiles)

  // input audio files
  const originalAudioFileUrl = audio_file_inputs && audio_file_inputs.length > 0 && audio_file_inputs[0].audio_file?.public_url
  const inputAudioFiles = audio_file_inputs && audio_file_inputs.length > 0 && audio_file_inputs


  const input_url = props.job.func_kwargs?.input_url
  const should_extract_lead_vocal = props.job.func_kwargs?.should_extract_lead_vocal

  const isPreview = props.job.type === "preview"

  const inferenceIcon = <FontAwesomeIcon icon={faMicrophoneStand} />
  const trainingIcon = <i className="fa-kit fa-thin-lips-circle-bolt fa-2xs"></i>

  const jobCardContainerRef = useRef(null) // FOr getting clientHeight and passing up to virtualized list
  const [toggleAnimationTimerId, setToggleAnimationTimerId] = useState(null)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const [isUpdateUsernameModalOpen, setIsUpdateUsernameModalOpen] = useState<boolean>(false)
  useEffect(() => {
    if (isSignedIn && userProfile && isLoaded && isShareModalOpen) {
      if (!userProfile.username) {
        setIsUpdateUsernameModalOpen(true);
      }
    }
  }, [isSignedIn, userProfile, isLoaded, isShareModalOpen])
  // Decide which audio file is the main one
  // If standard vocal conversion, choose first output audio file
  let mainAudioFileUrl = (vocalAudioFiles.length > 0 && vocalAudioFiles[0].audio_file?.public_url)
  // If song conversion (we extracted lead vocal), choose mixed output audio file
  if (should_extract_lead_vocal) mainAudioFileUrl = (mixAudioFiles.length > 0 && mixAudioFiles[0].audio_file?.public_url)
  // If stem split, use selected stem
  if (isStemSplit) {
    // Check which stem was selected first 
    const selectedStems = props.job.func_kwargs?.selected_stems
    const selectedStemAudioFiles = audio_file_outputs.filter(audio_file_output => selectedStems.some(stem => audio_file_output.type.includes(stem)))
    mainAudioFileUrl = selectedStemAudioFiles.length > 0 && selectedStemAudioFiles[0].audio_file?.public_url

  }
  if (isLyricToMusic) {
    mainAudioFileUrl = outputAudioFiles.length > 0 && outputAudioFiles[0].audio_file?.public_url
  }
  if (isSongGeneration) {
    mainAudioFileUrl = generatedSongFiles.length > 0 && generatedSongFiles[0].audio_file?.public_url
  }
  const isPlayingMainAudioFile = isPlaying && currentSong?.url === mainAudioFileUrl

  const shouldHideInputs = props.job?.func_kwargs?.should_hide_inputs

  const shareJobLink = window.location.origin + "/app/song/" + props.job.uuid;
  //  Determine job display name from original filenames and input url
  //  also COPIED TO InferPage component's queueNextAudioPlayback method to create DisplaySong object for queue
  let jobName = audio_file_outputs.length > 0 && audio_file_outputs[0].audio_file.filename.split('/').pop().slice(36)
  if (audio_file_inputs) jobName = audio_file_inputs.length > 0 && audio_file_inputs[0].audio_file?.original_filename
  if (input_url && !jobName) jobName = input_url

  // NEW FOR lyrics to music with sampler ref preset
  // Job.name can be set when being created on backend or in worker etc.
  if (props.job?.name) {
    jobName = props.job?.name
  }

  const fallbackImageUrl = isLyricToMusic ? DEFAULT_SAMPLER_STYLE_IMAGE_URL : DEFAULT_COVER_IMAGE_URL
  let jobImageUrl = props.job?.image_url || voice_model?.image_url || fallbackImageUrl
  const clearAnimationTimer = () => {
    // clear any animation timers to cancel notifying parents of size change
    if (toggleAnimationTimerId) {
      clearTimeout(toggleAnimationTimerId)
      setToggleAnimationTimerId(null)
    }
  }

  // Notify parent of collapse/expand so virtualized lists can adjust row heights
  const handleOpening = () => {
    setIsTransitioning(true)

    //clear timers
    clearAnimationTimer();

    // If we got a ref
    if (jobCardContainerRef.current) {
      // set timeout to let animation finish (200 ms)
      let timerId = setTimeout(() => {
        const clientHeight = jobCardContainerRef.current?.clientHeight
        props.afterExpand?.(clientHeight + 20)
        setIsTransitioning(false)
      }, 200);
      setToggleAnimationTimerId(timerId)
    }
  }

  // Notify parent of collapse/expand so virtualized lists can adjust row heights
  const handleClosing = () => {
    setIsTransitioning(true)

    //clear timers
    clearAnimationTimer();

    // If we got a ref
    if (jobCardContainerRef.current) {
      // set timeout to let animation finish (200 ms)
      let timerId = setTimeout(() => {
        const clientHeight = jobCardContainerRef.current?.clientHeight
        props.afterCollapse?.(clientHeight + 20)
        setIsTransitioning(false)
      }, 300);
      setToggleAnimationTimerId(timerId)
    }
  }


  const handlePlay = () => {
    // Create DisplaySong object for main preview
    const displaySong: DisplaySongInfo = {
      title: jobName,
      singer: isStemSplit ? " " : voice_model?.name,
      url: mainAudioFileUrl,
      coverUrl: jobImageUrl,
    }

    dispatch(globalAudioActions.setCurrentSong(displaySong))
    dispatch(globalAudioActions.setIsPlaying(true))
    props.onPlayed?.(props.job.uuid)
  }

  const handlePlayOriginal = () => {

    // Create DisplaySong object for main preview
    const displaySong: DisplaySongInfo = {
      title: jobName,
      singer: voice_model?.name,
      url: originalAudioFileUrl,
      coverUrl: jobImageUrl,
    }

    dispatch(globalAudioActions.setCurrentSong(displaySong))
    dispatch(globalAudioActions.setIsPlaying(true))
  }

  const handlePause = () => {
    dispatch(globalAudioActions.setIsPlaying(false))
  }

  const handleThumbsUp = async () => {
    const newRating = props.job.rating === 1 ? 0 : 1
    try {
      dispatch(jobsActions.updateJobRatingLocally({ uuid: props.job.uuid, rating: newRating }));
      await Api.rateJob(props.job.uuid, newRating);
    } catch {
      toast.error('Failed to rate output');
    }
  }

  const handleThumbsDown = async () => {
    const newRating = props.job.rating === -1 ? 0 : -1
    try {
      dispatch(jobsActions.updateJobRatingLocally({ uuid: props.job.uuid, rating: newRating }));
      await Api.rateJob(props.job.uuid, newRating);
    } catch {
      toast.error('Failed to rate output');
    }
  }

  // Get/make play button for original input audio file
  const isPlayingOriginal = isPlaying && currentSong?.url === originalAudioFileUrl
  const playOriginalButton = (
    <PlayOriginalButton>
      <Button
        sx={{ borderRadius: '10px' }}
        size="small" variant="text"
        onClick={!isPlayingOriginal ? handlePlayOriginal : handlePause}
        startIcon={!isPlayingOriginal ? <FontAwesomeIcon icon={faPlay} size="1x" /> : <FontAwesomeIcon icon={faPause} size="1x" />}
      >
        {!isPlayingOriginal ? 'PLAY ORIGINAL' : 'PAUSE'}
      </Button>
    </PlayOriginalButton>
  );

  // default to pulsing animation QUEUED
  let borderElement = <div className="animate-border-pulse"></div>
  let isJobComplete = false
  let statusElement = <JobStatus>Queued</JobStatus>
  let contentElement = null
  let mainHoverButton = (null)

  //  If status created, no output audio files to play
  if (status === 'CREATED') {
    borderElement = null
    isJobComplete = false
    statusElement = <JobStatus>Queued</JobStatus>
    mainHoverButton = (
      <MainPlayButton>
        <Pulsar color={'#FFF'} size={18} speed={1} />
      </MainPlayButton>
    )
  } else if (status === 'STARTED') {
    borderElement = <div className="animate-border-trail"></div>
    isJobComplete = false
    statusElement = <JobStatus>Processing</JobStatus>
    mainHoverButton = (
      <MainPlayButton>
        <Orbit color={'#FFF'} size={18} speed={1} />
      </MainPlayButton>
    )
  } else if (status === 'FAILED') {
    borderElement = null
    isJobComplete = false
    const errorCode = props.job?.func_results?.error_code;
    // THIS WIERD ASS SHIT
    // error codes global codes and messages are sent from backend in user profile object, dont ask why nobody knows
    if (isLoaded && userProfile?.error_codes?.hasOwnProperty(errorCode)) {
      statusElement = <JobStatus>{userProfile.error_codes[errorCode]}</JobStatus>
    } else {
      statusElement = <JobStatus>Failed - Please contact support at hello@controlla.xyz</JobStatus>
    }
    mainHoverButton = (
      <MainPlayButton>
        <FontAwesomeIcon icon={faCircleExclamation} />
      </MainPlayButton>
    )
  } else if (status === 'PENDING') {
    borderElement = null
    isJobComplete = false
    const errorCode = props.job?.func_results?.error_code;
    if (isLoaded && userProfile?.error_codes?.hasOwnProperty(errorCode)) {
      statusElement = <JobStatus>{userProfile.error_codes[errorCode]}</JobStatus>
    } else {
      statusElement = <JobStatus>Pending Upgrade/Payment</JobStatus>
    }
    mainHoverButton = (
      <MainPlayButton>
        <FontAwesomeIcon icon={faCircleExclamation} />
      </MainPlayButton>
    )
  } else if (status === 'COMPLETED') {
    borderElement = null
    isJobComplete = true
    mainHoverButton = (
      <MainPlayButton onClick={isPlayingMainAudioFile ? handlePause : handlePlay}>
        <FontAwesomeIcon icon={isPlayingMainAudioFile ? faPause : faPlay} />
      </MainPlayButton>
    )
    const isThumbsUp = props.job.rating === 1
    const isThumbsDown = props.job.rating === -1
    statusElement = (
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: "-4px" }}>
        <DetailsTrigger>
          {shouldHideInputs && (
            "Output "
          )}
          {!shouldHideInputs && (
            "Input/Output "
          )}
          <FontAwesomeIcon
            rotation={isDetailsCollapsed ? 0 : 180}
            icon={faChevronDown}
          />
        </DetailsTrigger>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <IconButton onClick={handleThumbsUp} size="small" sx={{ color: '#645270', '&:hover': { color: '#f5e1ff !important' } }}>
            <FontAwesomeIcon icon={isThumbsUp ? faThumbsUpSolid : faThumbsUp} />
          </IconButton>
          <IconButton onClick={handleThumbsDown} size="small" sx={{ color: '#645270', '&:hover': { color: '#f5e1ff !important' } }}>
            <FontAwesomeIcon icon={isThumbsDown ? faThumbsDownSolid : faThumbsDown} />
          </IconButton>
        </div>
      </div>
    );
    contentElement = <Collapsible
      transitionTime={200}
      open={!isDetailsCollapsed}
      onOpening={handleOpening}
      onClosing={handleClosing}
    >
      {/*{playOriginalButton}*/}
      <JobAudioFileList isHighlighted={true}>
        {outputAudioFiles && outputAudioFiles.map((audio_file_output, index) => {
          let audio_file = audio_file_output.audio_file;
          let audio_type = audio_file_output.type;
          if (audio_file.public_url === '') return null;
          let filename = audio_file.original_filename || audio_file.filename.split('/').pop().slice(36)
          let finalFilename = "controlla_" + voice_model?.name + "_" + filename
          if (isStemSplit) {
            if (jobName) {
              filename = audio_type + "_" + jobName.split(".")[0] + ".wav"
            }
            finalFilename = "controlla_" + filename
          }
          if (isLyricToMusic) {
            if (jobName) {
              // lyrics to music outputs are mp3 when unshifted
              filename = audio_type + "_" + jobName.split(".")[0] + ".mp3"
            }
            finalFilename = "controlla_" + filename
          }
          let audioUrl = audio_file.public_url;

          const displaySong: DisplaySongInfo = {
            title: filename,
            singer: isStemSplit ? undefined : voice_model?.name,
            url: audioUrl,
          }


          let waveformEl = (
            <>
              {/* @ts-ignore */}
              <AudioFileStreamable style={{ flexShrink: 1 }} uniqueKey={audio_file_output.uuid} audioFileId={audio_file.id} downloadCount={audio_file.download_count} url={audioUrl} filename={finalFilename} displaySong={displaySong} audioType={audio_file_output.type} audioFile={audio_file} />
            </>
          );

          return (
            <JobAudioFileListItem
              key={audio_file_output.uuid}
            >
              {waveformEl}
            </JobAudioFileListItem>
          );
        })}
      </JobAudioFileList>
      <JobAudioFileList style={{ marginBottom: '10px' }}>
        {/* Hide input files if input url or style ref */}
        {!shouldHideInputs && !input_url && inputAudioFiles && inputAudioFiles.map((audio_file_input, index) => {
          let audio_file = audio_file_input.audio_file;
          if (audio_file.public_url === '') return null;
          let filename = audio_file.filename.split('/').pop().slice(36)
          let audioUrl = audio_file.public_url;

          if (input_url) filename = audio_file_inputs.length > 0 && audio_file_inputs[0].audio_file?.original_filename || input_url

          const displaySong: DisplaySongInfo = {
            title: filename,
            singer: voice_model?.name,
            url: audioUrl,
          }

          let waveformEl = (
            <>
              {/* @ts-ignore */}
              <AudioFileStreamable style={{ flexShrink: 1 }} uniqueKey={audio_file_input.uuid} audioFileId={audio_file.id} downloadCount={audio_file.download_count} url={audioUrl} filename={filename} displaySong={displaySong} audioType={audio_file_input.type} audioFile={audio_file} />
            </>
          );

          return (
            <JobAudioFileListItem
              key={audio_file_input.uuid}
            >
              {waveformEl}
            </JobAudioFileListItem>
          );
        })}
      </JobAudioFileList>
      {/* <Divider variant="middle" component="div" /> */}

      {isLyricToMusic && (
        <LyricsRow>
          <LyricsContent>
            <LyricsTitleGroup>
              <FontAwesomeIcon icon={faAlignLeft} />
              <LyricsLabel>Lyrics</LyricsLabel>
            </LyricsTitleGroup>
            <LyricsPreview>
              {getTruncatedLyricsFromJob(props.job, 42)}
            </LyricsPreview>
          </LyricsContent>
          <LyricsButton onClick={() => setLyricsModalOpen(true)}>
            View Lyrics
          </LyricsButton>

          <Dialog
            open={lyricsModalOpen}
            onClose={() => setLyricsModalOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Lyrics</DialogTitle>
            <DialogContent>
              <LyricsText>
                {props.job?.func_kwargs?.lyrics}
              </LyricsText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setLyricsModalOpen(false)}>
                Done
              </Button>
            </DialogActions>
          </Dialog>
        </LyricsRow>
      )}
    </Collapsible>;
  }


  const thumbnailHoverActions = (
    <ThumbnailHoverActions>
      {/* Main play button plays/pauses preview in selected voice */}
      {mainHoverButton}
    </ThumbnailHoverActions>
  );

  let jobVoiceText
  let jobVoiceIcon
  if (voice_model) {
    jobVoiceText = voice_model.name
    if (isTraining) jobVoiceIcon = trainingIcon
    else jobVoiceIcon = inferenceIcon
  } else if (isStemSplit) {
    jobVoiceText = getSelectedStemsStringFromStemSplitJob(props.job)
    jobVoiceIcon = <img src={stemSplitIcon} alt='stem-split' />
  } else if (isLyricToMusic) {
    jobVoiceText = getTruncatedLyricsFromJob(props.job, 20)
    jobVoiceIcon = <FontAwesomeIcon icon={faCompactDisc} />
  }

  // Toggle details collapsed state
  const toggleDetails = (e) => {
    // Don't toggle if clicking on buttons or other interactive elements
    if (
      e.target.closest('button') ||
      e.target.closest('a') ||
      e.target.closest('.MuiIconButton-root')
    ) {
      return;
    }

    setIsDetailsCollapsed(!isDetailsCollapsed);
  };

  const handleMakePrivate = async () => {
    try {
      await Api.makeJobPrivate(props.job.uuid);
      toast.success("Song made private");
    } catch (err) {
      toast.error("Failed to make job private");
    }
  };

  const handleMakePublic = async () => {

    try {
      await Api.makeJobPublic(props.job.uuid)
      toast.success("Ready to share");
    } catch (err) {
      toast.error("Failed to make job public");
    }

  };



  return (
    <JobContainer
      isJobComplete={isJobComplete}
      ref={jobCardContainerRef}
      isTransitioning={isTransitioning}
      {...props}
    >
      <Job>
        <JobPreviewContent
          onClick={isJobComplete ? toggleDetails : undefined}
          isClickable={isJobComplete}
        >
          <JobThumbnail>
            <JobImage src={jobImageUrl} />
            {thumbnailHoverActions}
          </JobThumbnail>
          <JobContent>
            <JobHeader>
              <JobName>
                {jobName}
              </JobName>
              <ShareContainer>
                <ShareButton
                  startIcon={<FontAwesomeIcon icon={faShare} style={{marginRight: "-5px"}} size="2xs" />}
                  sx={{ background: '#645270', paddingLeft: '15px', textTransform: 'none' }}
                  onClick={() => {
                    if (status !== 'COMPLETED') return;
                    setIsShareModalOpen(true); setCopyLinkButtonText("COPY"); handleMakePublic();
                  }}
                >
                  Share
                </ShareButton>
                <UpdateUsernameModal open={isUpdateUsernameModalOpen} onClose={() => setIsUpdateUsernameModalOpen(false)} />
                <ShareDialog
                  isOpen={isShareModalOpen}
                  onClose={() => setIsShareModalOpen(false)}
                  name={userProfile?.username}
                  jobName={jobName}
                  shareUrl={shareJobLink}
                />
              </ShareContainer>
              <JobCardActionsMenu
                job={props.job}
                setIsShareModalOpen={setIsShareModalOpen}
                setCopyLinkButtonText={setCopyLinkButtonText}
                handleMakePublic={handleMakePublic}
                handleMakePrivate={handleMakePrivate}
                onAfterDeleteJob={props.onAfterDeleteJob}
              />
            </JobHeader>
            <JobSubheader>
              <JobVoice>
                <JobVoicePrefix>
                  {jobVoiceIcon}
                </JobVoicePrefix>
                <JobVoiceText>
                  {jobVoiceText}
                </JobVoiceText>
              </JobVoice>
            </JobSubheader>
            {statusElement}
          </JobContent>
        </JobPreviewContent>
        {borderElement}
        {/* Move Collapsible outside of JobContent but still inside JobContainer */}
        {isJobComplete && (
          <CollapsibleWrapper>
            {contentElement}
          </CollapsibleWrapper>
        )}
      </Job>

    </JobContainer>
  );
});

const KeepPublicButton = styled(Button)`
  color: var(--primary-contrast, #FFF);

  /* button/medium */
  font-family: Jura;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: 0.4px;
  text-transform: uppercase;
`;

const MakePrivateButton = styled(Button)`
  display: flex;
  width: 209px;
  height: 46px;
  padding: 6px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  border-radius: 15px;
  background: var(--Button-Active, linear-gradient(180deg, #7D0EB5 0%, #4D0671 100%));

  /* elevation/2 */
  box-shadow: 0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20);

  color: var(--primary-contrast, #FFF);
  /* button/medium */
  font-family: Jura;
  font-size: 1px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: 0.4px;
  text-transform: uppercase;

  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
`;

const PrivateConfirmationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 21px;
  padding:20px;
  justify-content: center;
`;

const InfoTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
p{
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  color: rgba(245, 225, 255, 0.74);
  font-feature-settings: 'liga' off, 'clig' off;
  text-overflow: ellipsis;

  /* typography/body2 */
  font-family: Raleway;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 143%; /* 20.02px */
  letter-spacing: 0.17px;
}
`;

const CopyLinkButton = styled(Button)`
  display: flex;
  height: 50px;
  padding: 6px 16px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: 15px;
  background: var(--Button-Active, linear-gradient(180deg, #7D0EB5 0%, #4D0671 100%));
  
  /* elevation/2 */
  box-shadow: 0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20);
`;

const LinkText = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width:0;
  max-width:350px;
  @media only screen and (max-width: 600px) {
    max-width:150px;
  }
  
`;

const LinkTextTypography = styled(Typography)`
  color: var(--text-primary, #FFF);
  font-feature-settings: 'liga' off, 'clig' off;
  /* typography/body1 */
  font-family: Raleway;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
  letter-spacing: 0.15px;
`;


const SharedLinkBox = styled.div`
  display: flex;
  height: 50px;
  padding: 25px 23px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  flex: 1 0 0;
  border-radius: 20px;
  border: 2px solid rgba(245, 225, 255, 0.74);
  background: #07000B;
`;

const ShareLinkContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 21px;
  align-self: stretch;
  margin-top:20px;
  margin-bottom:20px;
  margin:20px;
`;

const ShareButton = styled(Button)`
  display: flex;
  // width: 82px;
  height: 29px;
  padding: 20px;
  justify-content: center;
  align-items: center;
  gap: 5px;
  border-radius: 15px;
  background: #645270;

  /* elevation/2 */
  box-shadow: 0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20);
`;
const ShareContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Job = styled.div`
  // For animated border
  position: relative;
  z-index: 1;

  max-width: 1160px;
  width: 100%;
  min-height: 115px;
  margin-left: auto;
  margin-right: auto;
  
  background: #251A30;
  border-radius: 20px;
  border: 1px solid rgba(245, 225, 255, 0);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  transition: border 0.2s linear;

  &:hover {

    button {
      opacity: 1;
    }
  }

  h2 {
    text-align: left;
    font-size: 24px;
  }
`;

const JobPreviewContent = styled.div<{ isClickable?: boolean }>`
  // For animated border
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;

  padding: 20px;
  padding-bottom: 5px;
  width: 100%;
  
  /* Add cursor pointer when clickable */
  cursor: ${props => props.isClickable ? 'pointer' : 'default'};
  
  /* Add hover effect when clickable */
  ${props => props.isClickable && `
    &:hover {
      background-color: rgba(255, 116, 233, 0.05);
      border-radius: 20px;
    }
  `}
`;

const JobThumbnail = styled.div`
  width: 70px; //REQUIRES update MainPlayButton position offset if changing size
  height: 70px;
  position: relative;
  margin-right: 20px;
  margin-top: 0px;
  flex-shrink: 0;

  // For animated border
  z-index: 1;
`

const JobImage = styled.img`
  width: 100%;
  height: 100%;
  vertical-align: unset;
  border-radius: 10px;
`

const JobContent = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  min-width: 0;

  // For animated border
  z-index: 1; 
`;

const JobHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  

  i {
    margin-top: auto;
    margin-bottom: auto;
    padding-top: 5px;
    font-size: 16px;
  }
`;

const JobSubheader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;

`;

const JobName = styled.div`
  color: #FFFFFF;
  text-align: left;
  font-family: Raleway;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  flex-shrink: 1;
  flex-basis: 0;
  flex-grow: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  min-width: 0;
`;

const JobVoice = styled.div`
  text-align: left;
  font-size: 20px;
  color: rgba(245, 225, 255, 0.74);
  font-family: 'Jura';
  font-weight: lighter;
  flex-shrink: 1;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  min-width: 0;
`;

const JobVoicePrefix = styled.span`
  text-align: left;
  font-weight: 300;
  font-size: 14px;
  padding-right: 8px;
  flex-shrink: 0;

  display: flex;
  justify-content: center;
  flex-direction: column;

  img {
    height: 14px;
  }
`;

const JobVoiceText = styled.span`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  min-width: 0;
  color: rgba(245, 225, 255, 0.74);
  font-family: Raleway;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
`;

const JobAudioFileList = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  background-color: ${props => props.isHighlighted ? 'rgba(245, 225, 255, 0.06)' : 'transparent'};
`;

const JobStatus = styled.div`
  font-family: Raleway;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
`;

const PlayOriginalButton = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  min-width: 0;
  
  // {
  //  flex-grow: 0;
  //}
`;

const DetailsTrigger = styled.a`
  color: var(--text-disabled, rgba(245, 225, 255, 0.74));
  font-family: Raleway;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 150%; /* 21px */
  letter-spacing: 0.15px;

  pointer-events: none;

  
  &:hover {
    color: #FF74E9;
  }
`;

const MainPlayButton = styled.button`
  position: absolute;
  top: calc(50% - 25px); //center by subtract self height
  left: calc(50% - 25px); //center by subtract self width
  width: 50px;
  height: 50px;
  
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  
  border-radius: 36px;
  background: none;
  outline: none;
  border: none;

  color: #FFF;
  text-align: center;
  text-overflow: ellipsis;
  font-family: Raleway;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  
  
  .fa-play {
  }
  
  &:hover {
    background: #180d2399;

    .fa-play {
      color: #ff74e9;
    }
  }
`;

// needs to be defined for nested hover styled on SongPreviewCard
const ThumbnailHoverActions = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  
  z-index: 1;
  background: #0C081033;

  display: none; // on hover show (SongPreviewCard styles at top of this files' styled components)
`;

// Main container initialized last bc it references other styled components defined above
const JobContainer = styled.div<{ isJobComplete?: boolean; isTransitioning?: boolean; }>`
  z-index: ${props => props.isTransitioning ? 10 : 'unset'};
  
  // For animated border div
  position: relative;

  margin-bottom: 20px;
  margin-right: 20px;
  margin-left: 20px;
  
  // Add display flex and column direction to stack Job and Collapsible
  display: flex;
  flex-direction: column;
  align-items: stretch;

  border: 1px solid transparent;
  
  .animate-border-pulse::after {
    background: #251A30;
  }
  .animate-border-trail::after {
    background: #251A30;
  }

  // Make image and titles semi transparent while in progress
  ${JobImage} {
    opacity: ${props => props.isJobComplete ? 1 : 0.4};
  }
  ${JobHeader} {
    opacity: ${props => props.isJobComplete ? 1 : 0.4};
  }
  ${Job} {
    background: ${props => props.isJobComplete ? '#251A30' : 'none'};
    border: ${props => props.isJobComplete ? '1px solid rgba(245, 225, 255, 0)' : '1px solid rgba(245, 225, 255, 0.2)'} ;
  }
  
  
  &:hover {
    border-radius: 20px;

    ${Job} {
      border: 1px solid rgba(255, 116, 233, 0.99);
      background: ${props => props.isJobComplete ? '#251A30' : 'none'};
    }
    
    ${ThumbnailHoverActions} {
      display: block;
    }
  }
`

const JobAudioFileListItem = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
`;

const LyricsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 5px;
  padding: 20px;
`;

const LyricsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const LyricsTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(245, 225, 255, 0.74);
  
  svg {
    font-size: 14px;
  }
`;

const LyricsLabel = styled.span`
  font-family: Raleway;
  font-size: 14px;
  font-weight: 300;
`;

const LyricsPreview = styled.div`
  color: #FFF;
  font-family: Raleway;
  font-size: 14px;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const LyricsButton = styled(Button)`
  && {
    margin-left: 16px;
    height: fit-content;
    color: rgba(245, 225, 255, 0.74);
    border-color: rgba(245, 225, 255, 0.74);
    
    &:hover {
      border-color: #FF74E9;
      color: #FF74E9;
    }
  }
`;

const LyricsText = styled.pre`
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: Raleway;
  font-size: 14px;
  line-height: 1.6;
  color: #FFF;
  margin: 0;
  padding: 16px 0;
`;

// Add these new styled components
const CollapsibleWrapper = styled.div`
  width: 100%;
  padding-bottom: 5px;
  
  // Style the Collapsible component's container
  .Collapsible {
    width: 100%;
  }
`;

const CollapsibleContent = styled.div`
  padding: 0;
  background: #251A30;
  border-radius: 15px;
  width: 100%;
  box-sizing: border-box;
`;

// Add new styled components for the collapse button
const CollapseButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 15px;
`;

const CollapseButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(37, 26, 48, 0.8);
  color: rgba(245, 225, 255, 0.74);
  border: 1px solid rgba(245, 225, 255, 0.2);
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(37, 26, 48, 1);
    border-color: #FF74E9;
    color: #FF74E9;
  }
  
  span {
    font-family: Raleway;
    font-weight: 400;
  }
`;